import React from "react";
import { api } from "./api.jsx";
import { MEETUP_STATUS_NAMES, tradingName } from "./data.jsx";
import { useApp } from "./store.jsx";
import { PlacePicker, MeetWhen } from "./location.jsx";
import { hostingShop, hoursSummary } from "./hours.jsx";
import { SectionHead, MeetupCardsSkeleton, CardTraderMark, Dialog, useConfirm } from "./chrome.jsx";

const { Button, IconButton, Icon, Badge, Card, Tabs, MeetupCard, EmptyState, FluteDivider, Input, Radio } = window.CardboardmarketDesignSystem_2a3239;

/**
 * An instant, as the local wall-clock string a `datetime-local` field takes.
 *
 * The exact inverse of what `submit` below does with the value it reads back —
 * `new Date(when).toISOString()` — and it has to be, because that parse reads
 * the string in the BROWSER's zone. `formatMeetAt` in api.jsx deliberately
 * formats in Europe/Madrid, since a displayed date is a brand rule rather than a
 * locale one; borrowing that here would move the meeting by the offset between
 * the two whenever a device is not on Madrid time, and the only person who would
 * ever find out is the one standing outside a café an hour early.
 *
 * Nothing anywhere parses the formatted string back. The raw instant travels
 * from the API as `meetAt` precisely so that nothing ever has to.
 */
function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** One line of what the other party suggested: a glyph, the value, its hint. */
function ProposedLine({ icon, label, value, note }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-start' }}>
      <span style={{ color: 'var(--text-muted)', display: 'flex', paddingTop: 2 }}><Icon name={icon} size={18} /></span>
      <div>
        <div className="cbm-label" style={{ marginBottom: 2 }}>{label}</div>
        <div style={{ font: 'var(--type-ui)', fontSize: 'var(--text-md)', color: 'var(--text-strong)' }}>{value}</div>
        {note ? <div style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>{note}</div> : null}
      </div>
    </div>
  );
}

/**
 * Answering a proposal.
 *
 * This was a form titled "Where and when?", which asked the person ACCEPTING to
 * DEFINE a meetup — when in the ordinary case the proposer had already named a
 * place and a time, and all that was wanted was a yes. The time was not even
 * prefilled: `when` started empty and the confirm button stayed disabled until
 * the recipient retyped a time that was sitting on the screen behind the dialog.
 * The cause was a layer away, in api.jsx, which handed this screen the time
 * already formatted for reading and kept no instant a field could take.
 *
 * So it has two faces. With a complete proposal to answer it SHOWS what was
 * suggested and offers one obvious yes, with changing the details as a quieter
 * second path. With nothing to confirm — both halves are optional on propose —
 * it is the form it always was, because there is no statement to make and an
 * empty "they suggested:" panel with blanks in it says nothing at all.
 *
 * CHANGING THE DETAILS IS STILL ACCEPTING, and the copy says so out loud. It
 * sends nothing back to the proposer: the trade goes to `accepted` on the terms
 * in the form and they are never asked again.
 *
 * This paragraph used to end "a real back-and-forth needs the trade to know
 * whose turn it is — a column, a transition, and rules about who may move — and
 * none of that exists yet". It exists now: migration 065 added `awaiting_id`,
 * and CounterDialog below is the back-and-forth. What is still true is that
 * changing the details HERE is not it — accepting on different terms ends the
 * negotiation, countering continues it, and the two must not look alike.
 */
/**
 * The trade itself, on the screen where somebody says yes to it.
 *
 * THE GAP, reported by the owner: "when trying to accept the offer, the dialog
 * shown as a confirmation should have all the details of the offer. Not only
 * the meetup details, but the offer itself". The dialog asked about a café and
 * a time and never once said which cards were changing hands or for how much —
 * so the one moment where the terms become binding was the one moment they were
 * not on screen.
 *
 * AND THEY REALLY DO BECOME BINDING, which is why it is worth showing here
 * rather than trusting somebody to remember the card behind the dialog. Nothing
 * can move these afterwards: countering requires the trade to still be
 * `proposed`, the accept transition writes only the place and the time, and the
 * price was fixed onto the trade when it was proposed. The copy says so, and it
 * is true rather than reassuring.
 */
function OfferTerms({ trade }) {
  const { t } = useApp();
  const give = trade.offer.items.filter((i) => i.direction === 'out');
  const get = trade.offer.items.filter((i) => i.direction === 'in');
  const cash = trade.offer.cash;
  const label = (i) => (i.grade ? `${i.title} \u00b7 ${i.grade}` : i.title);

  const Side = ({ head, list }) => (
    list.length ? (
      <div>
        <span className="cbm-label">{head}</span>
        <ul style={{ margin: 0, paddingLeft: '1.2em', font: 'var(--type-body)' }}>
          {list.map((i) => <li key={i.id || i.title}>{label(i)}</li>)}
        </ul>
      </div>
    ) : null
  );

  return (
    <div style={{
      border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-sm)',
      padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)',
    }}>
      <Side head={t("You give")} list={give} />
      <Side head={t("You get")} list={get} />
      {/*
        Copies only when there is more than one. A line reading "1" on every
        single-card trade is noise on the screen where noise is most expensive.
      */}
      {trade.quantity > 1 ? (
        <div style={{ font: 'var(--type-body)' }}>
          {t("{n} copies", { n: trade.quantity })}
        </div>
      ) : null}
      {cash ? (
        <div style={{ font: 'var(--type-body)' }}>
          {cash.youPay
            ? t("You pay {amount} on top.", { amount: api.formatCents(cash.cents) })
            : t("They pay {amount} on top.", { amount: api.formatCents(cash.cents) })}
        </div>
      ) : null}
      {/*
        ONLY WHERE THE OFFER ITSELF NAMES NO MONEY.
        
        The money in a trade is the cash on the offer — that is the number the
        two of them actually negotiated, and since a counter replaces the whole
        offer it cannot drift. `agreedPrice` is a different fact: the LISTING's
        asking price, pinned when the trade was proposed (migration 074). It
        exists for trades made before the proposal form started putting the
        asking price into the offer, where it is the only record of the money.
        
        Showing both would read as a sum. A €5 card proposed at €5 would say
        "you pay €5,00" and "agreed at €5,00" one line apart, and somebody would
        arrive with ten.
      */}
      {!cash && trade.offer.agreedPrice ? (
        <div style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
          {t("Agreed at {total}", { total: api.formatCents(trade.offer.agreedPrice.totalCents) })}
        </div>
      ) : null}
    </div>
  );
}

function AcceptDialog({ trade, onClose, onAccept }) {
  const { myShop, t } = useApp();
  /*
   * Whether there is something here to say yes to WITHOUT typing.
   *
   * Both halves, because the server refuses an accept that would leave a trade
   * agreed with only one of them. So a proposal naming a café and no time has
   * nothing that can be taken as it stands, and the honest thing to show is the
   * form, carrying the half they did give.
   *
   * A SHOP'S COUNTER IS BOTH HALVES (the owner, 2026-10-17): its name is the
   * place and its opening hours are the time, which is the server's rule for an
   * accept too. Read off the trade (`atCounter`, settled when it was proposed)
   * AND a shop still hosting it here: a counter switched off since needs a
   * time again, and a one-tap yes the server would answer `meetup_required`
   * is not one.
   */
  const host = hostingShop(trade.theirShop, myShop);
  const offered = Boolean(trade.place)
    && (Boolean(trade.meetAt) || (trade.atCounter && Boolean(host)));
  const [editing, setEditing] = React.useState(!offered);
  const [placeName, setPlaceName] = React.useState(trade.place || '');
  const [placeNote, setPlaceNote] = React.useState(trade.address || '');
  const [when, setWhen] = React.useState(toLocalInput(trade.meetAt));
  /**
   * An optional pin for the meeting place.
   *
   * Deliberately not a places database and not Google Places. A meeting point is
   * written once by two people who then have to find it — an autocomplete over
   * every business in Spain buys very little, and it would send each of those
   * searches to a third party, in a product whose whole design is about not
   * revealing where anybody is.
   *
   * A name, a hint, and somewhere on a map is the whole requirement.
   */
  const [pin, setPin] = React.useState(trade.placePoint);
  const [busy, setBusy] = React.useState(false);
  const them = trade.them.displayName.split(' ')[0];

  /*
   * AND NOT A TIME THE SHOP IS SHUT. The field says so in red as soon as one is
   * chosen; this is what stops it being sent, because the server refuses it and
   * a round trip to learn what the screen already knew is a round trip.
   */
  /*
   * A TIME IS REQUIRED UNLESS A SHOP IS HOSTING, which is the same rule the
   * server keeps: "the café" with no hour is not an arrangement, and a counter
   * with published opening hours is.
   */
  const can = placeName.trim().length > 0 && (when.length > 0 || Boolean(host));

  /**
   * Yes, exactly as it stands.
   *
   * Sends NOTHING, and that is the point. The server keeps every field the
   * proposer set when the accept body omits it — place, note, pin and time
   * alike — so "as offered" is the absence of an opinion rather than this screen
   * reading four values off the proposal and posting them back, which would be
   * four chances to send one of them subtly changed. An accept that quietly
   * moves the time is a thing to design out, not a thing to test for.
   */
  const acceptAsOffered = async () => {
    setBusy(true);
    try {
      await onAccept({});
      onClose();
    } finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      // datetime-local has no zone. The trader is agreeing to a wall-clock time in
      // the place they are standing, so it is read as local and sent as an instant.
      //
      // ONLY WHEN THERE IS ONE. Where a shop hosts there is no time field and
      // `when` is empty, and `new Date('').toISOString()` throws — inside the
      // click, before any request, so "Accept and confirm" did nothing at all.
      // Omitted, the server keeps no time for a counter and the old one otherwise.
      await onAccept({
        placeName: placeName.trim(),
        placeNote: placeNote.trim() || undefined,
        ...(when ? { meetAt: new Date(when).toISOString() } : {}),
        ...(pin ? { placeLat: pin[0], placeLng: pin[1] } : {}),
      });
      onClose();
    } finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 60 }}>
        <Dialog open closeLabel={t("Close")} width={440} onClose={onClose} data-cbm="accept-as-offered"
          title={t("{name} suggested a meetup", { name: them })}
          description={t("Accepting fixes the trade on these terms. Nothing is charged through the app.")}
          footer={<><Button variant="ghost" onClick={() => setEditing(true)} disabled={busy}>{t("Change the details")}</Button>
                   <Button onClick={acceptAsOffered} disabled={busy}>{busy ? t("One moment\u2026") : t("Accept as offered")}</Button></>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <OfferTerms trade={trade} />
            {/*
              No map, and not an oversight.

              A pin can only be set by whoever ACCEPTS: `placeLat`/`placeLng` are
              on the transition route and not on propose, and the propose dialog
              passes `PlacePicker` no `onPin`, so it never even offers one. A
              trade being answered here is `proposed`, so its `place_point` is
              always null and a map would have nothing to draw, every time.

              `placePoint` still seeds the pin in the form below, so the day a
              proposal can carry one this screen already keeps it rather than
              dropping it on the way through.
            */}
            {/*
              The address AND a way to get there, because this is the screen
              where somebody decides whether they can actually go.
              
              A name on its own — "Café Comercial" — is not enough to judge a
              journey, and the note beneath it is whatever the proposer typed,
              which may be an address or may be "by the fountain". The link is
              the only part that always resolves to somewhere.

              Opened in a new tab: this dialog holds an unanswered question, and
              navigating away from it loses the answer.
            */}
            <ProposedLine icon="map-pin" label={t("Where")} value={trade.place} note={trade.address} />
            {trade.directions ? (
              <a href={trade.directions} target="_blank" rel="noopener noreferrer"
                 style={{ font: 'var(--type-meta)', color: 'var(--ink-700)', marginLeft: 34 }}>
                {t("Open in maps")}
              </a>
            ) : null}
            {/*
              At a counter the time is the shop's hours, so they are what this
              line shows: "while open" over the week as the door writes it.
              The shop answering from its own back office reads "you".
            */}
            {trade.when || !host ? (
              <ProposedLine icon="clock" label={t("When")} value={trade.when} />
            ) : (
              <ProposedLine icon="clock" label={t("When")}
                value={trade.hostedByYou ? t("While you are open") : t("While the shop is open")}
                note={hoursSummary(host.hours, t)} />
            )}
          </div>
        </Dialog>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60 }}>
      <Dialog open closeLabel={t("Close")} width={440} onClose={onClose}
        title={offered ? t("Change the details") : t("Where and when?")}
        /*
         * Said out loud, because the mechanism is not what anybody would assume.
         * Changing these and accepting settles the trade on THESE terms — it is
         * not a counter-offer, and the other party gets no further say. Somebody
         * who believed they were suggesting an alternative would in fact be
         * agreeing to meet at a time the other person has never seen.
         */
        description={offered
          ? t("Changing these accepts the trade on your terms. {name} is not asked again.", { name: them })
          : t("Pick somewhere public that suits you both. Nothing is charged through the app.")}
        footer={<><Button variant="ghost" onClick={offered ? () => setEditing(false) : onClose} disabled={busy}>
                   {offered ? t("Back") : t("Cancel")}
                 </Button>
                 <Button onClick={submit} disabled={!can || busy}>{busy ? t("One moment\u2026") : t("Accept and confirm")}</Button></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          {/* Here too. This branch also ends in an accept, and it is the one
              where somebody is busy typing a time and least likely to have the
              cards in mind. */}
          <OfferTerms trade={trade} />
          <PlacePicker
            name={placeName} onName={setPlaceName}
            note={placeNote} onNote={setPlaceNote}
            pin={pin} onPin={setPin}
            // Their shop first, where they have one. See PlacePicker.
            theirShop={trade.theirShop}
            // And this account's own counter, for a shop answering a person.
            myShop={myShop}
          />
          {/* No hour where a shop is hosting — its opening hours are the time. */}
          {hostingShop(trade.theirShop, myShop)
            ? null
            : <MeetWhen value={when} onChange={setWhen} host={null} />}
        </div>
      </Dialog>
    </div>
  );
}

/**
 * Reporting the other party.
 *
 * Deliberately a separate, deliberate action rather than a variant of cancelling.
 * Anyone may change their mind about a trade and nothing should happen to them for
 * it; this is for saying somebody behaved badly, and it is worth making that feel
 * different to use.
 */
// Module scope: these hold the English KEY. t() here would run once at load,
// before a locale exists, and never again when somebody switches language.
const REPORT_REASONS = [
  { value: 'no_show', label: 'They did not turn up' },
  { value: 'misrepresented', label: 'The card was not as described' },
  { value: 'counterfeit', label: 'The card was fake' },
  { value: 'aggressive', label: 'They behaved badly' },
  { value: 'other', label: 'Something else' },
];

function ReportDialog({ trade, onClose }) {
  const { notify, loadTrades, t } = useApp();
  const [reason, setReason] = React.useState('no_show');
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.reportTrade(trade.id, reason, note.trim() || undefined);
      notify(t("Reported. Thank you — we read these."));
      /* The trade now says Reported and offers no second report: asked again
         rather than waiting for whatever next reloads the list. */
      loadTrades();
      onClose();
    } catch (e) {
      notify(e.message, 'caution');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60 }}>
      <Dialog open closeLabel={t("Close")} width={460}
        title={t("Report {name}", { name: trade.them.displayName })}
        description={t("For when something actually went wrong. Changing your mind is not this — withdraw instead, and nothing happens to either of you.")}
        onClose={onClose}
        footer={<><Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button>
                 <Button onClick={submit} disabled={busy}>{busy ? t("Sending\u2026") : t("Report")}</Button></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          <Radio value={reason} onChange={setReason} options={REPORT_REASONS.map((r) => ({ ...r, label: t(r.label) }))} />
          <Input label={t("What happened (optional)")} placeholder={t("Waited 40 minutes at the café.")}
            value={note} onChange={(e) => setNote(e.target.value)} />
          <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
            {t("One report per trade. It goes to us, not to them.")}
          </span>
        </div>
      </Dialog>
    </div>
  );
}

/**
 * What you can DO with a trade, wherever the trade is being shown.
 *
 * Buttons come from `can`, which the server computes. The client does not decide
 * who may do what — it would only be guessing at rules the API enforces anyway,
 * and the two would drift.
 *
 * That is also why this is shared rather than written twice. The conversation
 * used to carry its own version of these buttons: `can.accept ? Accept : Mark
 * done`, a binary that quietly assumed every trade in the thread was still live.
 * It read correctly only for as long as the thread showed nothing else, and the
 * moment it showed completed and cancelled trades too, every one of them grew a
 * greyed-out "Mark done" — an offer to finish something already finished or
 * called off. Deriving from `can` has no such state to be wrong about: a trade
 * nobody can act on renders nothing at all.
 *
 * `onMessage` is OPTIONAL, and that is the whole of what separates the two
 * callers. The Trades screen has somewhere to send you, so it passes one; the
 * conversation is already the place that button goes, so it does not. A
 * `hideMessage` flag would say the same thing from the wrong end — the caller
 * would be naming a button it does not own instead of simply not having anywhere
 * to navigate.
 *
 * The dialogs are here rather than at the screen, because they belong to the
 * buttons that open them and a screen showing a list of trades would otherwise
 * have to hold "which one is being accepted" on behalf of every row.
 */
/**
 * Answering a proposal with a different one.
 *
 * WHAT IT LETS YOU CHANGE, AND WHY NOT EVERYTHING. What you are ASKING FOR stays
 * as it is: you are answering a proposal about a particular card, and a dialog
 * that let you change both halves would not be a counter, it would be a new
 * proposal wearing one's clothes. What moves is your side of it — which of your
 * cards you put up, and the cash.
 *
 * CASH IS THE OTHER HALF OF HAGGLING, and it can go either way here. Until
 * migration 065 it could only flow from whoever proposed first, so "your card
 * for mine and I will add five euros" was not expressible by the person most
 * likely to say it.
 */
/**
 * What identifies one thing in an offer.
 *
 * The card where there is one, and only then the title. A title is not an
 * identity — a trader can hold the same card three times over, in three
 * printings, under one name — but it is the only handle left for an item the
 * catalogue never knew, or one whose card row has since been deleted.
 *
 * Prefixed so the two can never collide: a card called "card:x" is a card, not
 * a uuid.
 */
function entryKey(x) {
  return x.cardId ? `card:${x.cardId}` : `title:${x.title}`;
}

/** One selected thing: how to recognise it, and what to send it back as. */
function offerEntry(x) {
  return { key: entryKey(x), title: x.title, cardId: x.cardId || null };
}

function CounterDialog({ trade: m, onClose, onCounter }) {
  const { myCards, t } = useApp();
  // `m.offer` is the offer as data; `m.items` is the same thing formatted for
  // reading, with the grade glued into the name and cash folded in as a row.
  /*
   * SELECTED BY CARD, NOT BY NAME. Reported from production: a trader holding
   * three listings of One Piece ST15-002 — same card code, different printings,
   * one title — opened this dialog on a proposal for ONE of them and found all
   * three ticked. Sending it would have handed over two cards nobody asked for,
   * and the row already shows the set and the foil marker, so it would have
   * looked like a deliberate choice rather than a bug.
   *
   * Every entry carries its own title as well as its card, because an item whose
   * card row is gone still has to go back out with the name it was promised
   * under. That is also why the state holds objects rather than ids: the title
   * cannot be looked up again from a list the card has dropped out of.
   */
  const [offer, setOffer] = React.useState(
    m.offer.items.filter((i) => i.direction === 'out').map(offerEntry),
  );
  const [cash, setCash] = React.useState(m.offer.cash ? String(m.offer.cash.cents / 100) : '');
  const [cashFrom, setCashFrom] = React.useState(m.offer.cash && !m.offer.cash.youPay ? 'them' : 'me');
  const [busy, setBusy] = React.useState(false);
  const [filter, setFilter] = React.useState('');

  // What they are giving you, which this dialog does not touch.
  const asking = m.offer.items.filter((i) => i.direction === 'in');
  const mine = (myCards || []).filter((c) => c.status === 'active');
  /*
   * Matched against the set as well as the title, because the set is exactly
   * what distinguishes two listings of one card — filtering only by name would
   * leave the ambiguity this list exists to remove.
   *
   * Anything already ticked stays visible whatever the filter says: a selection
   * that disappears while you narrow the list is a selection you cannot undo,
   * and you would send it without seeing it.
   */
  const needle = filter.trim().toLowerCase();
  const picked = (c) => offer.some((x) => x.key === entryKey(c));
  const shown = needle
    ? mine.filter((c) =>
        picked(c) ||
        // The set and the foil marker are searchable too, because they are what
        // somebody types when two rows share a name.
        `${c.title} ${c.set ?? ''} ${c.foil ? 'foil' : ''}`.toLowerCase().includes(needle))
    : mine;

  const toggle = (c) =>
    setOffer((prev) => prev.some((x) => x.key === entryKey(c))
      ? prev.filter((x) => x.key !== entryKey(c))
      : [...prev, offerEntry(c)]);

  const send = async () => {
    setBusy(true);
    try {
      const cents = cash.trim() === '' ? undefined : Math.round(Number(cash.replace(',', '.')) * 100);
      await onCounter({
        items: [
          // Kept exactly as they are: a counter answers a question, it does not
          // change what was asked.
          // The card travels with it. A counter replaces every item, so dropping
          // it here would un-identify what the trade is for on the first haggle.
          ...asking.map((i) => ({
            direction: 'requested', title: i.title, ...(i.cardId ? { cardId: i.cardId } : {}),
          })),
          ...offer.map((x) => ({
            direction: 'offered', title: x.title, ...(x.cardId ? { cardId: x.cardId } : {}),
          })),
        ],
        ...(cents ? { cashCents: cents, cashFrom } : {}),
      });
      onClose();
    } finally { setBusy(false); }
  };

  /*
   * WHAT MUST BE TRUE TO SEND: something is DIFFERENT. Not "you have added a
   * card of your own".
   *
   * Requiring a card was my mistake and the owner caught it: a buyer haggling
   * over five euros has nothing to add and nothing to swap — they want the same
   * card for four. That is a counter, it is probably the commonest kind, and the
   * server has always accepted it (the requested side alone satisfies the
   * schema). Only this dialog refused.
   *
   * So the button asks the only question worth asking: is this actually a
   * different offer from the one on the table?
   */
  const wasOffering = m.offer.items.filter((i) => i.direction === 'out').map(entryKey).sort().join('|');
  const nowOffering = offer.map((x) => x.key).sort().join('|');
  const wasCash = m.offer.cash ? String(m.offer.cash.cents) : '';
  const nowCash = cash.trim() === '' ? '' : String(Math.round(Number(cash.replace(',', '.')) * 100));
  const wasPayer = m.offer.cash ? (m.offer.cash.youPay ? 'me' : 'them') : '';
  const nowPayer = nowCash === '' ? '' : cashFrom;
  const changed = wasOffering !== nowOffering || wasCash !== nowCash || wasPayer !== nowPayer;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60 }}>
      <Dialog open closeLabel={t("Close")} width={480} onClose={onClose}
        title={t("Offer something different")}
        description={t("They asked for this trade. Change what you put up, and it goes back to them to answer.")}
        footer={<><Button variant="ghost" onClick={onClose} disabled={busy}>{t("Cancel")}</Button>
                 <Button onClick={send} disabled={busy || !changed}>
                   {busy ? t("One moment\u2026") : t("Send it back")}
                 </Button></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          <div>
            <span className="cbm-label">{t("You get")}</span>
            <ul style={{ margin: 0, paddingLeft: '1.2em', font: 'var(--type-body)' }}>
              {asking.map((i) => <li key={i.id}>{i.title}</li>)}
            </ul>
            {/*
              WHAT WAS AGREED, AND NOT WHAT THE LISTING SAYS TODAY.
              
              The price is fixed onto the trade when it is proposed (migration
              074), so a seller editing their listing afterwards does not move
              it. Said out loud here because the whole point of fixing it is that
              somebody can rely on it — a guarantee nobody can see is one nobody
              acts on.
              
              Absent for a listing offered for trade, which has no price, and for
              trades proposed before that migration.
            */}
            {!m.offer.cash && m.offer.agreedPrice ? (
              <div style={{ font: 'var(--type-meta)', color: 'var(--text-muted)', marginTop: 4 }}>
                {t("Agreed at {total}", { total: api.formatCents(m.offer.agreedPrice.totalCents) })}
              </div>
            ) : null}
          </div>

          <div>
            <span className="cbm-label">{t("You give")}</span>
            {mine.length ? (
              <>
                {/*
                  A FILTER, because this list is as long as somebody's inventory.
                  The owner has enough listings on pre-production that the dialog
                  ran off the bottom of the screen; a hundred checkboxes is not a
                  list anybody reads, it is one they scroll past.
                */}
                {mine.length > 6 ? (
                  <Input
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder={t("Filter your cards")}
                    style={{ marginTop: 'var(--space-2)' }}
                  />
                ) : null}
                {/*
                  BOUNDED AND SCROLLING, so the dialog cannot outgrow the window.
                  40vh rather than a pixel height: the constraint is the phone,
                  and a fixed height that fits one screen overflows a smaller one.
                */}
                <div style={{
                  display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
                  marginTop: 'var(--space-2)', maxHeight: '40vh', overflowY: 'auto',
                }}>
                  {shown.map((c) => (
                    /*
                      ENOUGH TO TELL TWO OF THE SAME CARD APART, which a name
                      alone is not. The owner's case: several listings of one
                      card from different sets or with different alternate art —
                      a checkbox list of identical titles is one somebody ticks
                      the wrong row in, and the first they would know is at the
                      meetup.

                      So: the picture, the set, the grade, and a link to the
                      listing itself for anything this row does not show.
                    */
                    <label key={c.id} style={{
                      display: 'flex', gap: 'var(--space-3)', alignItems: 'center',
                      font: 'var(--type-body)', padding: 'var(--space-2)',
                      border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-sm)',
                    }}>
                      <input type="checkbox" checked={picked(c)} onChange={() => toggle(c)} />
                      {c.image ? (
                        <img src={api.pickWidth(c.imageSrcset, 240) || c.image} alt="" loading="lazy"
                          style={{ width: 34, height: 46, objectFit: 'contain', borderRadius: 2, background: 'var(--kraft-100)' }} />
                      ) : (
                        <span style={{ width: 34, height: 46, borderRadius: 2, background: 'var(--kraft-100)' }} />
                      )}
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block' }}>{c.title}</span>
                        <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
                          {/* Foil belongs here for the same reason the set does: it is
                              what tells two listings of one card apart. */}
                          {[c.set, c.condition, c.foil ? t("Foil") : null].filter(Boolean).join(' · ') || t("No set named")}
                        </span>
                      </span>
                      {/* New tab: this dialog holds an unfinished offer. */}
                      <a href={`/listing/${c.id}`} target="_blank" rel="noopener noreferrer"
                         onClick={(e) => e.stopPropagation()}
                         style={{ font: 'var(--type-meta)', color: 'var(--ink-700)', whiteSpace: 'nowrap' }}>
                        {t("View")}
                      </a>
                    </label>
                  ))}
                  {!shown.length ? (
                    <p style={{ font: 'var(--type-meta)', color: 'var(--text-muted)', margin: 0 }}>
                      {t("Nothing matches that.")}
                    </p>
                  ) : null}
                </div>
              </>
            ) : (
              <p style={{ font: 'var(--type-meta)', color: 'var(--text-muted)', margin: 0 }}>
                {t("You have nothing listed to offer. Post a card first, or add cash below.")}
              </p>
            )}
            {!changed ? (
              /* `marginTop` because the list above it is a bordered box: without
                 it the sentence sits against the edge, which the owner spotted. */
              <p role="status" style={{
                font: 'var(--type-meta)', color: 'var(--text-faint)',
                margin: 0, marginTop: 'var(--space-3)',
              }}>
                {t("Change a card or the cash to send this back.")}
              </p>
            ) : null}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <span className="cbm-label">{t("Cash, if any")}</span>
            {/* Said out loud, because the dialog used to refuse this: haggling on
                price alone is a counter, and probably the commonest one. */}
            <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
              {t("On its own this is a price counter \u2014 same cards, different money.")}
            </span>
            {/* The number and the direction together: an amount with no side is
                the ambiguity migration 065 exists to remove. */}
            <Input value={cash} onChange={(e) => setCash(e.target.value)} inputMode="decimal" placeholder="5,00" />
            {cash.trim() !== '' ? (
              <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
                <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', font: 'var(--type-meta)' }}>
                  <input type="radio" name="cashFrom" checked={cashFrom === 'me'} onChange={() => setCashFrom('me')} />
                  {t("I pay")}
                </label>
                <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', font: 'var(--type-meta)' }}>
                  <input type="radio" name="cashFrom" checked={cashFrom === 'them'} onChange={() => setCashFrom('them')} />
                  {t("They pay")}
                </label>
              </div>
            ) : null}
          </div>
        </div>
      </Dialog>
    </div>
  );
}

/**
 * What moved since the last offer.
 *
 * THE GAP THIS CLOSES, reported by the owner after countering himself from a
 * second account: "I see the new price, but not what it was before, so I don't
 * know if it was lower, higher, how much". A counter replaces the offer
 * outright, so the card he was reading was complete and still told him nothing —
 * the number on it was true, and useless without the one it replaced.
 *
 * ONLY WHAT CHANGED. The offer itself is already on the card above this; a
 * second full copy of it would double the reading and bury the one line that
 * matters. So: the money, the copies, and the cards that came and went.
 *
 * A card is matched by its own id where it has one, exactly as the counter
 * dialog selects by it. Matching on titles would report a swap between two
 * printings of ST15-002 as no change at all, which is the same bug in a
 * different coat.
 */
function WhatChanged({ trade: m }) {
  const { t } = useApp();
  const prev = m.previousOffer;
  if (!prev) return null;

  const keyOf = (i) => (i.cardId ? `card:${i.cardId}` : `title:${i.title}`);
  const now = m.offer.items;
  const before = prev.items;
  const nowKeys = new Set(now.map(keyOf));
  const beforeKeys = new Set(before.map(keyOf));
  const added = now.filter((i) => !beforeKeys.has(keyOf(i)));
  const gone = before.filter((i) => !nowKeys.has(keyOf(i)));

  /*
   * Cash is compared as BOTH halves: how much, and who hands it over. A counter
   * that leaves the number alone and turns the payment around is a completely
   * different deal, and reading only the cents would call it unchanged.
   */
  const cents = (c) => (c ? c.cents : 0);
  const moneyMoved = cents(prev.cash) !== cents(m.offer.cash)
    || Boolean(prev.cash?.youPay) !== Boolean(m.offer.cash?.youPay);
  const copiesMoved = prev.quantity !== m.quantity;

  if (!added.length && !gone.length && !moneyMoved && !copiesMoved) return null;

  const money = (c) => (c
    ? (c.youPay ? t("you pay {amount}", { amount: api.formatCents(c.cents) })
                : t("they pay {amount}", { amount: api.formatCents(c.cents) }))
    : t("no cash"));

  const Row = ({ label, children }) => (
    <div style={{ display: 'flex', gap: 'var(--space-2)', font: 'var(--type-meta)' }}>
      <span style={{ color: 'var(--text-muted)', minWidth: '5.5em' }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
    </div>
  );

  return (
    <div style={{
      border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-sm)',
      padding: 'var(--space-3)', marginBottom: 'var(--space-3)',
      display: 'flex', flexDirection: 'column', gap: 'var(--space-1)',
    }}>
      <span className="cbm-label">
        {prev.byYou ? t("What you changed") : t("What they changed")}
      </span>
      {moneyMoved ? <Row label={t("Cash")}>{money(prev.cash)} → {money(m.offer.cash)}</Row> : null}
      {copiesMoved ? <Row label={t("Copies")}>{prev.quantity} → {m.quantity}</Row> : null}
      {added.length ? <Row label={t("Added")}>{added.map((i) => i.title).join(', ')}</Row> : null}
      {gone.length ? <Row label={t("Dropped")}>{gone.map((i) => i.title).join(', ')}</Row> : null}
    </div>
  );
}

/**
 * WHERE THIS TRADE'S COPIES STAND ON CARDTRADER — for the shop whose stock it
 * is, and for nobody else: the server sends `cardtrader` only to that shop
 * (ADR 0011, ticket 09). It sends a code and counts; the sentence is made here.
 *
 * A line rather than a badge, because half of them carry a reason or a thing to
 * do, and those are sentences. Amber where the shop has something to look at —
 * a copy still on sale there, a copy already sold there, a description that did
 * not come back — and muted where it is only news.
 *
 * Exported for the back office's Agreed list, which shows the same line.
 */
export function CardTraderLine({ trade, style }) {
  const { t } = useApp();
  const c = trade && trade.cardtrader;
  if (!c) return null;

  const why = () => {
    switch (c.reason) {
      case 'token_refused': return t("CardTrader no longer accepts your token");
      case 'upstream_unavailable': return t("CardTrader was not answering");
      case 'timeout': return t("CardTrader did not answer in time");
      case 'product_not_found': return t("the product is no longer on CardTrader");
      case 'disconnected': return t("you disconnected CardTrader");
      case 'switched_off': return t("updating your CardTrader stock was switched off");
      default: return t("something went wrong at our end");
    }
  };

  let text = null;
  let attention = false;
  switch (c.line) {
    case 'taking_off': text = t("Waiting to be taken off CardTrader."); break;
    case 'held_by_switch':
      text = t("Not taken off CardTrader: updating your stock there is switched off.");
      attention = true;
      break;
    case 'checking_take_off': text = t("Sent to CardTrader without an answer. We are checking whether it came off."); break;
    case 'taken_off': text = t("Taken off CardTrader."); break;
    case 'already_sold':
      text = c.copies === 0
        ? t("Already sold on CardTrader: there was nothing left to take off. The trade is still on — decide it with the trader.")
        : t("Already sold on CardTrader: only {taken} of {asked} copies were left to take off. The trade is still on — decide it with the trader.",
            { taken: c.copies, asked: c.copies + c.shortfall });
      attention = true;
      break;
    case 'not_taken_off':
      text = t("Could not be taken off CardTrader: {why}. The copies are still on sale there, so take them off by hand.", { why: why() });
      attention = true;
      break;
    case 'putting_back': text = t("Waiting to be put back on CardTrader."); break;
    case 'checking_put_back': text = t("Sent back to CardTrader without an answer. We are checking whether it arrived."); break;
    case 'put_back': text = t("Put back on CardTrader."); break;
    case 'put_back_as_new':
      text = t("Put back on CardTrader as a new product. Check its description there: it did not come back.");
      attention = true;
      break;
    case 'not_put_back':
      text = t("Not put back on CardTrader: {why}. Put the copies back there by hand.", { why: why() });
      attention = true;
      break;
    default: return null;
  }

  return (
    <div data-cbm="cardtrader-line" data-line={c.line}
         style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', marginBottom: 'var(--space-3)', ...style }}>
      <CardTraderMark height={11} alt="" />
      <span style={{ font: 'var(--type-meta)', color: attention ? 'var(--status-caution)' : 'var(--text-muted)', flex: 1, minWidth: 0 }}>
        {text}
        {c.recordedOnly ? ` ${t("Only recorded: this was never sent to CardTrader.")}` : null}
      </span>
    </div>
  );
}

/*
 * THREE OF THE ACTIONS ARE ICONS, and only those three. The owner, from a
 * screenshot of an accepted trade at desktop width: "Buttons are too much. For
 * 'See the card' and 'Directions' we could use icon buttons ... Same thing for
 * the 'Message' one" — six labelled buttons and Message wrapped onto a line of
 * its own. Those three are places to GO, each with an icon that already said so
 * (a picture, a pin, a speech bubble); the ones that CHANGE the trade — Accept,
 * Mark done, Withdraw, Report — keep their words, because a mis-tap on those is
 * not undone by pressing back.
 *
 * The label does not disappear: IconButton puts it on `aria-label` and `title`,
 * so a screen reader and a getByRole both still find "See the card", and a mouse
 * gets it as a tooltip.
 *
 * ONE HEIGHT FOR THE WHOLE ROW: IconButton's own `sm`, 32 px square, the height
 * of the labelled `sm` buttons beside them. These were sized to --touch-min
 * (44 px) once, and the owner, 2026-10-17: "we should aim to normalize button
 * heights. We can't have 44px icons when the normal buttons are 32px tall in
 * full and 44px only cover the icons themselves, not extra spacings of the
 * button." A 44 px target on three of six buttons protects nothing the other
 * three do not already leave exposed, and it made the row 12 px taller than
 * its skeleton (`meetup-card`, whose action bars are --control-h-sm) and its
 * own labelled buttons. If the row ever needs bigger
 * targets, every button in it grows together — not by an inline style here.
 */

/**
 * ACCEPTING, WITH THE QUESTION IT MAY NEED: where and when. One implementation
 * for every place a trade is accepted — `TradeActions` asks it at once; a
 * shop's Requests row accepts in one tap and asks it only when the server
 * refuses with `meetup_required` (a proposal naming no place and no time,
 * which a shop without a checked counter can receive). `ask(trade)` opens
 * `AcceptDialog`; `dialog` is the element to render, null while closed.
 */
export function useAccepting({ onAccepted } = {}) {
  const { actOnTrade } = useApp();
  const [accepting, setAccepting] = React.useState(null);
  const dialog = accepting ? (
    <AcceptDialog
      trade={accepting}
      onClose={() => setAccepting(null)}
      onAccept={async (meetup) => {
        const view = await actOnTrade(accepting.id, 'accept', meetup);
        if (onAccepted) onAccepted(view);
        return view;
      }}
    />
  ) : null;
  /*
   * ONE TAP, AND THE QUESTION ONLY IF IT IS NEEDED: tries the trade as it
   * stands, and a `meetup_required` refusal opens the dialog instead of a
   * "something went wrong" (the store is told the caller answers it).
   * Resolves to the accepted view, or null when the dialog was opened.
   */
  const accept = async (trade) => {
    try {
      const view = await actOnTrade(trade.id, 'accept', {}, { answers: ['meetup_required'] });
      if (onAccepted) onAccepted(view);
      return view;
    } catch (e) {
      if (e && e.code === 'meetup_required') { setAccepting(trade); return null; }
      throw e;
    }
  };
  return { accept, ask: setAccepting, dialog };
}

/**
 * THE QUESTION BEFORE A TRADE IS ENDED — declined, or withdrawn by whoever
 * proposed it (the owner, 2026-10-23: a confirmation before every destructive
 * action). Neither can be taken back: an ended proposal is not reopened, the
 * other side is told, and a new one starts from nothing. Shared by
 * `TradeActions` and the shop's Requests rows, so the two ask the same thing.
 *
 * AND MARK DONE, the owner, 2026-10-23: "Mark done" cannot be undone, so it
 * asks first. One side's mark is permanent (the server refuses a second one
 * and never takes it back), and the second side's completes the trade and
 * takes the copies off the listing.
 */
export function endTradeQuestion(trade, action, t) {
  if (action === 'complete') {
    return {
      title: t("Mark this trade done?"),
      description: t("Only once the cards have changed hands. It cannot be undone: when you have both marked it, the copies come off the listing."),
      confirmLabel: t("Yes, it is done"),
    };
  }
  // The name they trade under: a shop's own, a person's as given.
  const name = tradingName(trade.them);
  const description = name
    ? t("{name} is told, and this proposal cannot be reopened.", { name })
    : t("The other side is told, and this proposal cannot be reopened.");
  return action === 'decline'
    ? { title: t("Decline this trade?"), description, confirmLabel: t("Decline trade") }
    : { title: t("Withdraw this proposal?"), description, confirmLabel: t("Withdraw proposal") };
}

export function TradeActions({ trade: m, onMessage, listingSec, inRequests }) {
  const { actOnTrade, go, goRequests, t } = useApp();
  const { ask: setAccepting, dialog: acceptDialog } = useAccepting();
  const [countering, setCountering] = React.useState(null);
  const [reporting, setReporting] = React.useState(null);
  const [confirm, confirmDialog] = useConfirm();
  const endTrade = async (action) => {
    if (await confirm(endTradeQuestion(m, action, t))) actOnTrade(m.id, action);
  };

  /*
   * An ARRAY rather than a fragment, so "no actions" is a state this can be in.
   * A fragment of nothing but nulls still renders a wrapper, and a wrapper inside
   * a gapped column is a blank row under a trade that is over — visible, and
   * exactly as wrong as the disabled button it replaced.
   */
  const actions = [
    /*
     * WHICH CARD THIS IS ABOUT, and it belongs here rather than on either screen.
     *
     * A proposal lists what is being swapped and who you are meeting, and never
     * said which listing it came from — so the answer to "which one of their
     * cards was this again?" was to go and find it. The trade has always known:
     * `trade.listing_id` is on the row, the API has always returned `listingId`,
     * and the client mapper has always carried it. Nothing rendered it.
     *
     * In TradeActions because this component is the one thing the trades list
     * and the conversation share — the owner's own framing, that they are the
     * same proposal component — so one button appears in both places and cannot
     * drift between them.
     *
     * FIRST, before the decisions. Looking at the card is what somebody does
     * BEFORE accepting or countering, and a control you need first should not be
     * hiding behind the ones it informs.
     *
     * Absent when the listing is gone: `listing_id` is ON DELETE SET NULL, so a
     * withdrawn card leaves a real trade with nothing to point at, and a button
     * leading to a 404 is worse than no button.
     *
     * `listingSec` is the shop's back office saying which section to keep lit
     * (`?sec=requests`) while the card is open; absent everywhere else.
     */
    m.listingId ? (
      <IconButton key="listing" icon="image" variant="ghost" label={t("See the card")} size="sm"
        onClick={() => go('listing', { id: m.listingId, sec: listingSec })} />
    ) : null,
    m.can.accept ? <Button key="accept" size="sm" onClick={() => setAccepting(m)}>{t("Accept")}</Button> : null,
    // Between yes and no, which is where most trades actually live.
    m.can.counter ? <Button key="counter" size="sm" variant="outline" onClick={() => setCountering(m)}>{t("Offer something else")}</Button> : null,
    /*
     * DECLINE IS AN ICON, the x (the owner, 2026-10-19: "the buttons still
     * take too much space ... use an icon for the decline option too maybe
     * and add some tooltips"). Its name is on aria-label and, through
     * IconButton's title, in the tooltip a pointer gets. Accept and "Offer
     * something else" keep their words: they are the decisions.
     */
    m.can.decline ? (
      <IconButton key="decline" icon="x" size="sm" variant="ghost" label={t("Decline")}
        onClick={() => endTrade('decline')} />
    ) : null,
    m.can.complete ? <Button key="complete" size="sm" iconLeft="check" onClick={() => endTrade('complete')}>{t("Mark done")}</Button> : null,
    // Directions matter more than anything else here: this is the moment
    // somebody is actually going somewhere. A plain maps URL, so it opens in
    // whatever app they already use and costs nothing until it is tapped.
    // Not to your own counter: a shop does not need directions to itself.
    m.directions && m.status === 'accepted' && !m.hostedByYou ? (
      <IconButton key="directions" icon="map-pin" variant="outline" label={t("Directions")} size="sm"
        onClick={() => window.open(m.directions, '_blank', 'noopener,noreferrer')} />
    ) : null,
    m.can.cancel && !m.can.accept ? <Button key="cancel" size="sm" variant="ghost" onClick={() => endTrade('cancel')}>{t("Withdraw")}</Button> : null,
    // The server decides, not a status list copied into the client.
    // It also knows whether YOU already reported this one — which the client
    // did not, so the button stayed and a second report looked like it landed.
    // It never did: the insert has always been idempotent. The lie was here.
    m.can.report ? (
      <Button key="report" size="sm" variant="ghost" onClick={() => setReporting(m)}>{t("Report")}</Button>
    ) : null,
    onMessage ? (
      <IconButton key="message" icon="message-circle" variant="outline" label={t("Message")} size="sm"
        onClick={onMessage} />
    ) : null,
    /*
     * A SHOP reading a trade in a conversation can open it in its Requests,
     * where the rest of its trades are (shop trades view, ticket 08). The
     * conversation stays a place to answer it; this is the way to the list.
     */
    inRequests ? (
      <Button key="requests" size="sm" variant="ghost" iconLeft="arrow-left-right"
        onClick={() => goRequests({ id: m.id })}>{t("Open in Requests")}</Button>
    ) : null,
  ].filter(Boolean);

  /*
   * STATEMENTS ABOUT THE TRADE, ON THEIR OWN LINE — not mixed in with the things
   * you can press.
   *
   * "Waiting for Asier" sat in the same wrapping row as the buttons, so on a
   * phone it took the first slot and shoved Directions and Withdraw along until
   * Report and Message fell to a second line. The owner sent a screenshot of it:
   * "the label should be in its own row instead of making all buttons jump
   * down".
   *
   * It is also the honest grouping. A badge saying whose turn it is answers a
   * question; a button does something. Putting them in one row asks somebody to
   * work out which of these five things is even tappable, and on a narrow screen
   * the answer moves every time the trade changes state.
   */
  const status = [
    m.status === 'accepted' && !m.can.complete ? (
      <Badge key="waiting" tone="caution">{t("Waiting for {name}", { name: m.them.displayName.split(' ')[0] })}</Badge>
    ) : null,
    // Same reasoning: this replaced the Report button once it was used, so it
    // inherited a slot in a row of buttons while being the opposite of one.
    !m.can.report && m.youReported ? (
      <span key="reported" style={{ font: 'var(--type-meta)', color: 'var(--text-faint)' }}>{t("Reported")}</span>
    ) : null,
    /*
     * WHY IT ENDED, where nobody pressed Decline: the listing was taken down,
     * which declines what was still proposed on it (ticket 14). The server
     * sends the code; the sentence is made here.
     */
    m.status === 'declined' && m.cancelReason === 'listing_withdrawn' ? (
      <span key="withdrawn" data-cbm="ended-because" style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
        {t("The listing was withdrawn.")}
      </span>
    ) : null,
  ].filter(Boolean);

  return (
    <>
      {/* Above the buttons, because it is the thing you need in order to press
          one of them. */}
      <WhatChanged trade={m} />
      <CardTraderLine trade={m} />
      {status.length ? (
        <div style={{
          display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'center',
          // Only when buttons follow. A finished trade shows the badge alone and
          // a gap under it would hang off the bottom of the card.
          marginBottom: actions.length ? 'var(--space-3)' : 0,
        }}>{status}</div>
      ) : null}
      {actions.length ? (
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'center' }}>{actions}</div>
      ) : null}

      {reporting ? <ReportDialog trade={reporting} onClose={() => setReporting(null)} /> : null}
      {confirmDialog}

      {countering ? (
        <CounterDialog
          trade={countering}
          onClose={() => setCountering(null)}
          onCounter={(offer) => actOnTrade(countering.id, 'counter', offer)}
        />
      ) : null}

      {/* Agreeing a meetup settles a place and a time, so it asks for both. The
          same dialog opens from the Trades screen and from the conversation:
          somebody who is talking about a trade should not have to go and find it
          in a list to accept it. */}
      {acceptDialog}
    </>
  );
}

/** A trade that is over, however it ended: the trader's Past and the shop's. */
export const isPast = (x) => ['completed', 'declined', 'cancelled'].includes(x.status);

const TRADE_TABS = ['active', 'proposed', 'past'];

export function TradesScreen() {
  const { route, trades, tradesState, loadTrades, go, navigate, messageTrader , t } = useApp();
  const [tab, setTab] = React.useState(TRADE_TABS.includes(route.tab) ? route.tab : 'active');
  /*
   * THE TAB IN THE LINK, so a reload or a shared link opens the same one
   * (owner, 2026-10-22). Pushed, as the You tabs are: Back steps to the tab
   * you were on (owner, 2026-10-22: "let's unify navigation behaviors").
   * Filters and sorts replace; a tab is a step. The trade the address named is
   * dropped with it — the reader has moved off it, and kept, it would pull the
   * tab back to its own on the next render.
   */
  const pickTab = (value) => {
    setTab(value);
    navigate('trades', value === 'active' ? {} : { tab: value }, { keepScroll: true });
  };
  /* Back and Forward move the address under the screen; the tab follows it. */
  React.useEffect(() => {
    if (!route.id) setTab(TRADE_TABS.includes(route.tab) ? route.tab : 'active');
  }, [route.tab, route.id]);
  const groups = {
    active: trades.filter((t) => t.status === 'accepted'),
    proposed: trades.filter((t) => t.status === 'proposed'),
    past: trades.filter(isPast),
  };

  /*
   * ONE TRADE, NAMED IN THE ADDRESS.
   *
   * `/trades?id=<trade>` opens the tab that holds it and brings it into view.
   * Asked for by the owner for the shop's back office — "taking the user
   * navigating as a shop to the specific trade in their trades section" — and
   * it is worth having for everybody: until now this screen could only be
   * linked to as a whole, so a notification about one trade landed on a page of
   * them and left the reader to find it.
   *
   * THE TAB FOLLOWS THE TRADE rather than the other way round. A link to an
   * accepted trade that opened on Proposed would be a link to an empty screen,
   * and whoever sent it would have no way of knowing.
   */
  const named = route.id ? trades.find((x) => x.id === route.id) : null;
  const tabOf = (x) => (x.status === 'accepted' ? 'active'
    : x.status === 'proposed' ? 'proposed' : 'past');
  React.useEffect(() => {
    if (named) setTab(tabOf(named));
  }, [named && named.id, named && named.status]);

  /*
   * Brought into view once it is actually rendered, and only when it is off
   * screen: a scroll on a card already in front of somebody is a page moving
   * for no reason they can see.
   */
  React.useEffect(() => {
    if (!named) return;
    const el = document.querySelector(`[data-trade="${named.id}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [named && named.id, tab]);

  const list = groups[tab] || [];
  const openThread = messageTrader;

  return (
    <>
      <SectionHead title={t("Trades")} sub={t("Every trade ends with two people in the same place. Confirm the time, then hand it over.")} />
      <Tabs label={t("Trades")} value={tab} onChange={pickTab} style={{ marginBottom: 'var(--space-6)' }} items={[
        { value: 'active', label: t("Active"), count: groups.active.length },
        { value: 'proposed', label: t("Proposed"), count: groups.proposed.length },
        { value: 'past', label: t("Past"), count: groups.past.length },
      ]} />
      {list.length ? (
        <div className="cbm-grid-2">
          {list.map((m) => (
            <MeetupCard
              key={m.id}
              /* A handle for the link above, and for a browser test that wants
                 one trade rather than whichever happens to be first. */
              data-trade={m.id}
              data-named={named && named.id === m.id ? '1' : undefined}
              place={m.place || t("Not agreed yet")}
              address={m.address}
              /*
                A trade at a shop's counter has no hour and is not missing one:
                its time is the shop's opening hours. Saying "time not agreed
                yet" over a finished arrangement is the one way the owner's
                simplification could read as a regression.
              */
              when={m.when || (m.atCounter ? t("While the shop is open") : t("Time not agreed yet"))}
              withWhom={m.withWhom}
              items={m.items}
              status={m.cardStatus}
              // See inbox.jsx: the tone is the design system's, the words are ours.
              statusLabel={t(MEETUP_STATUS_NAMES[m.cardStatus] || MEETUP_STATUS_NAMES.proposed)}
              // This screen HAS somewhere to send you, so it passes a handler and
              // gets the Message button. The conversation does not — see TradeActions.
              action={<TradeActions trade={m} onMessage={() => openThread(m.them.id, m.them.displayName)} />}
            />
          ))}
        </div>
      )
        /*
         * Loading is not "you have no trades", and a failure is not either.
         *
         * All three tabs ran straight to their empty state while the list was
         * still being fetched — each with a Browse button under it. This is the
         * screen where a false empty answer is most likely to be believed,
         * because most people genuinely do have no trades most of the time, so
         * "nothing here" reads as confirmation rather than as news.
         *
         * The whole LIST, not the tab's slice. `list` is one tab of three and is
         * legitimately empty on two of them while the third is full, so gating on
         * it would put placeholders under "Past" on every poll.
         */
        : tradesState === 'error' ? (
          <EmptyState icon="alert-triangle" title={t("We could not load your trades")}
            description={t("Something went wrong at our end, not yours. Nothing has been agreed or cancelled.")}
            action={<Button onClick={() => loadTrades()}>{t("Try again")}</Button>} />
        ) : tradesState === 'loading' && !trades.length ? (
          <MeetupCardsSkeleton label={t("Loading your trades.")} />
        ) : (
        <EmptyState icon="arrow-left-right" title={tab === 'active' ? t("No meetups agreed yet") : tab === 'proposed' ? t("Nothing waiting on a reply") : t("No finished trades yet")}
          description={tab === 'past' ? t("Completed hand-offs stay here with what changed hands.") : t("Find a card within your radius and propose a swap \u2014 most replies come the same day.")}
          action={<Button onClick={() => go('browse')}>{t("Browse nearby cards")}</Button>} />
      )}

      {tab === 'active' && list.length ? (
        <section style={{ marginTop: 'var(--space-10)' }}>
          <FluteDivider style={{ marginBottom: 'var(--space-5)' }} />
          <div style={{ display: 'flex', gap: 'var(--space-5)', alignItems: 'center', flexWrap: 'wrap' }}>
            <Icon name="shield-check" size={20} />
            <span style={{ font: 'var(--type-body)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', maxWidth: 560 }}>
              {t("Meet in a public place, check condition against the photos before money changes hands, and mark the trade done together. Nothing is charged through the app.")}
            </span>
          </div>
        </section>
      ) : null}
    </>
  );
}

// TradeActions is what the conversation imports, and AcceptDialog is no longer
// exported because it travels inside it. The conversation needs the whole set of
// buttons, not just the dialog behind one of them: a trade shown in a thread is
// the same trade shown on this screen, and two places deciding separately what
// may be done to it is how one of them ends up offering to finish a trade that
// was cancelled a fortnight ago. That is not a hypothetical — it is what the
// thread did until this component existed.
//
// The one other place that accepts — a shop's Requests row, in one tap — takes
// the dialog through `useAccepting`, the same hook TradeActions uses, rather
// than an export of AcceptDialog: what accepting asks stays in one place.
